/**
 * useReachability — distinguishes "browser thinks it's offline" from
 * "Supabase isn't reachable" (captive portal, transient 5xx, expired CDN).
 *
 * Why this hook on top of useOnlineStatus:
 *   - navigator.onLine flips when the OS network state changes, but a
 *     captive portal will pass that check and still kill every fetch.
 *   - A long backend outage (Supabase) doesn't flip navigator.onLine at
 *     all, so the user sees a "we're online" UI while every action fails.
 *
 * Strategy:
 *   - Trust navigator.onLine for the *fast* signal — when it flips offline
 *     we trust it immediately. Saves a needless probe.
 *   - When it says online, run a periodic HEAD against the Supabase REST
 *     base URL (15s cadence, 5s timeout). A failure flips us to
 *     "online-but-unreachable" and the banner explains the difference.
 *   - On the back-to-online edge, we refire TanStack Query refetches via
 *     queryClient.invalidateQueries({ refetchType: "none" }) — pages set
 *     up `refetchOnReconnect: true` separately so they pick this up.
 *
 * Returns:
 *   { online, reachable, lastProbeAt }
 *
 *   - online:    navigator.onLine
 *   - reachable: online AND last Supabase probe succeeded
 *   - lastProbeAt: epoch ms, for diagnostics in the banner
 */
import { useEffect, useState } from "react";

const PROBE_PATH       = "/rest/v1/"; // returns 200 with no body
const PROBE_INTERVAL   = 15_000;
const PROBE_TIMEOUT_MS = 5_000;

interface Reachability {
  online:      boolean;
  reachable:   boolean;
  lastProbeAt: number | null;
}

export function useReachability(): Reachability {
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
  const anonKey     = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? "";

  const [state, setState] = useState<Reachability>(() => ({
    online:      typeof navigator === "undefined" ? true : navigator.onLine,
    reachable:   true, // optimistic
    lastProbeAt: null,
  }));

  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const probe = async () => {
      // If the browser already says offline, skip the probe — it'll fail
      // and the banner is already correct.
      if (!navigator.onLine) {
        if (cancelled) return;
        setState((s) => ({ ...s, online: false, reachable: false, lastProbeAt: Date.now() }));
        return;
      }
      if (!supabaseUrl) {
        // Without a configured Supabase URL there's nothing we can probe.
        // Assume reachable and let real fetches surface failures.
        if (cancelled) return;
        setState((s) => ({ ...s, online: true, reachable: true, lastProbeAt: Date.now() }));
        return;
      }

      const ctl = new AbortController();
      const to  = setTimeout(() => ctl.abort(), PROBE_TIMEOUT_MS);
      try {
        const res = await fetch(`${supabaseUrl}${PROBE_PATH}`, {
          method:  "HEAD",
          headers: anonKey ? { apikey: anonKey } : undefined,
          signal:  ctl.signal,
        });
        clearTimeout(to);
        if (cancelled) return;
        // We treat any 2xx/3xx/4xx as "reachable" — only network failure +
        // 5xx counts as unreachable. A 401 still means the server is up.
        const reachable = res.status < 500;
        setState({ online: true, reachable, lastProbeAt: Date.now() });
      } catch {
        clearTimeout(to);
        if (cancelled) return;
        setState({ online: navigator.onLine, reachable: false, lastProbeAt: Date.now() });
      }
    };

    const onOnline  = () => { setState((s) => ({ ...s, online: true }));  void probe(); };
    const onOffline = () => { setState((s) => ({ ...s, online: false, reachable: false })); };

    window.addEventListener("online",  onOnline);
    window.addEventListener("offline", onOffline);

    // Initial probe + periodic.
    void probe();
    timer = setInterval(probe, PROBE_INTERVAL);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      window.removeEventListener("online",  onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [supabaseUrl, anonKey]);

  return state;
}
